import {
  AUDIO_DEVICE,
  AUDIO_INPUT_DEVICE_KEY,
  AUDIO_INPUT_KIND,
  AUDIO_OUTPUT_DEVICE_KEY,
  AUDIO_OUTPUT_KIND,
  DEFAULT_AUDIO_DEVICE_ID,
  DEFAULT_VIDEO_DEVICE_ID,
  VIDEO_DEVICE,
  VIDEO_INPUT_DEVICE_KEY,
  VIDEO_INPUT_KIND,
} from '@tabeeb/modules/presentation/constants'

export const checkForDevices = async () => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return [AUDIO_DEVICE, VIDEO_DEVICE]
  }

  const devices = await navigator.mediaDevices.enumerateDevices()

  const deviceKinds = getDeviceKinds(devices)
  const audioDevice = getAudioDevice(deviceKinds)
  const videoDevice = getVideoDevice(deviceKinds)

  return [audioDevice, videoDevice]
}

export const getDevices = async (kind: MediaDeviceKind) => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return []
  }

  const devices = await navigator.mediaDevices.enumerateDevices()
  const specificDevices = devices.filter((device) => device.kind === kind)

  return specificDevices
}

export const getAudioInputDevices = async () => {
  const defaultDevices = ['default', 'communications']

  const devices = await getDevices(AUDIO_INPUT_KIND)

  return devices.filter((device) => !defaultDevices.includes(device.deviceId))
}

export const getAudioOutputDevices = async () => {
  const defaultDevices = ['default', 'communications']
  const devices = await getDevices(AUDIO_OUTPUT_KIND)

  return devices.filter((device) => !defaultDevices.includes(device.deviceId))
}

export const getVideoInputDevices = async () => {
  const devices = await getDevices(VIDEO_INPUT_KIND)

  return devices
}

export const getDeviceById = async (deviceId: MediaDeviceInfo['deviceId']) => {
  if (!navigator.mediaDevices || !navigator.mediaDevices.enumerateDevices) {
    return undefined
  }

  const devices = await navigator.mediaDevices.enumerateDevices()

  return devices.find((d) => d.deviceId === deviceId)
}

const getAudioDevice = (deviceKinds: MediaDeviceInfo['kind'][]) => {
  return deviceKinds.indexOf(AUDIO_INPUT_KIND) !== -1 ? AUDIO_DEVICE : ''
}

const getVideoDevice = (deviceKinds: MediaDeviceInfo['kind'][]) => {
  return deviceKinds.indexOf(VIDEO_INPUT_KIND) !== -1 ? VIDEO_DEVICE : ''
}

const getDeviceKinds = (devices: MediaDeviceInfo[]) => {
  return devices.map((device) => device.kind)
}

export const getAudioInputDeviceId = async (isDeviceSwitchingEnabled: boolean = false) => {
  const audioInputDevices = await getAudioInputDevices()
  const storedAudioInputDeviceId = localStorage.getItem(AUDIO_INPUT_DEVICE_KEY)
  const audioInputDeviceId =
    (storedAudioInputDeviceId && audioInputDevices.some((d) => d.deviceId === storedAudioInputDeviceId)
      ? storedAudioInputDeviceId
      : null) ||
    (isDeviceSwitchingEnabled && audioInputDevices[0] ? audioInputDevices[0].deviceId : DEFAULT_AUDIO_DEVICE_ID)

  return audioInputDeviceId
}

export const getAudioOutputDeviceId = async (isDeviceSwitchingEnabled: boolean = false) => {
  const audioOutputDevices = await getAudioOutputDevices()
  const storedAudioOutputDeviceId = localStorage.getItem(AUDIO_OUTPUT_DEVICE_KEY)
  const audioOutputDeviceId =
    (storedAudioOutputDeviceId && audioOutputDevices.some((d) => d.deviceId === storedAudioOutputDeviceId)
      ? storedAudioOutputDeviceId
      : null) ||
    (isDeviceSwitchingEnabled && audioOutputDevices[0] ? audioOutputDevices[0].deviceId : DEFAULT_AUDIO_DEVICE_ID)

  return audioOutputDeviceId
}

export const getVideoInputDeviceId = async () => {
  const videoDevices = await getVideoInputDevices()
  const storedVideoInputDeviceId = localStorage.getItem(VIDEO_INPUT_DEVICE_KEY)
  const videoInputDeviceId =
    (storedVideoInputDeviceId && videoDevices.some((d) => d.deviceId === storedVideoInputDeviceId)
      ? storedVideoInputDeviceId
      : null) || (videoDevices[0] ? videoDevices[0].deviceId : DEFAULT_VIDEO_DEVICE_ID)

  return videoInputDeviceId
}
